import React, { useState, useEffect } from "react";
import {
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ExpandIcon from "../../../assets/icons/arrow-right-black.svg";
import reckittIcon from "../../../assets/images/reckitt-logo.png";
import { NAVIGATION } from "./constants";
import { useAppMe } from "../../../services/okta";
import { CircularProgressCustom } from "../../../utils/muiCustomTheme";
import { ReactComponent as LogOutIcon } from "../../../assets/icons/log-out-icon.svg";
import { ReactComponent as IconHideSidebar } from "../../../assets/icons/Icon-hide-sidebar.svg";
import { ReactComponent as IconMenuOpen } from "../../../assets/icons/icon-menu-open.svg";
import { apiURL } from "../../env";
import "./LeftNavigation.scss";
import { useSimulationParams } from "../../hooks/useSimulationParams";

// --- Types ---

/**
 * Navigation item descriptor used to render the sidebar tree.
 * - title: Display text for the item
 * - segment: Path segment used for routing and active state detection
 * - children: Optional nested navigation items
 * - hover/normal: Optional icon components for hover/normal states
 * - adminOnly: Restricts visibility to admin roles when true
 */
export interface NavItemType {
  title: string;
  segment: string;
  children?: NavItemType[];
  hover?: React.ElementType;
  normal?: React.ElementType;
  adminOnly?: boolean;
}

/**
 * Props for the NavItem component.
 * - item: Current navigation node
 * - depth: Tree nesting level (used for indentation)
 * - openParent: Segment key of the currently expanded parent item
 * - setOpenParent: Setter to control expanded parent segment
 * - centerText: Centers the text for child items
 */
interface NavItemProps {
  item: NavItemType;
  depth: number;
  openParent: string | null;
  setOpenParent: React.Dispatch<React.SetStateAction<string | null>>;
  centerText?: boolean;
}

// --- Constants ---

/**
 * Static logout action injected at the bottom of the sidebar.
 * Uses a button element to avoid client-side navigation.
 */
const LOGOUT_ITEM: NavItemType = {
  segment: "logout",
  title: "Log out",
  normal: LogOutIcon,
  hover: LogOutIcon,
  adminOnly: false,
};

/**
 * Roles allowed to see items marked as adminOnly.
 */
const ALLOWED_ADMIN_ROLES = ["developer", "global_admin", "marketer_admin"];

// --- Main Component ---

/**
 * LeftNavigation renders the application sidebar with role-aware navigation items.
 * It:
 * - fetches user groups via useAppMe to determine admin visibility
 * - displays a loading spinner until user info is available
 * - filters NAVIGATION items based on admin-only flags
 * - renders nested items with expand/collapse behavior
 * - includes a logout action at the bottom
 */
export default function LeftNavigation() {
  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const navigate = useNavigate();
  const { createLink } = useSimulationParams();
  const [openParent, setOpenParent] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(isCollapsed));
  }, [isCollapsed]);

  const ccdp_groups: string[] = dataAppMe?.data?.ccdp_groups || [];
  const isAdmin = ccdp_groups.some((g) => {
    const groupLower = g.toLowerCase();
    return ALLOWED_ADMIN_ROLES.some((role) => groupLower.includes(role));
  });
  if (isPendingAppMe) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  const filteredNavigation = NAVIGATION.filter((item: NavItemType) => {
    if (isAdmin) return true;
    return !item.adminOnly;
  });

  return (
    <div className={`sidebar ${isCollapsed ? "sidebar--collapsed" : ""}`}>
      <div className="sidebar__icon-wrap">
        <div className="sidebar__icon-wrap__logo">
          <span className="sidebar__icon-wrap__logo__icon">
            <img
              onClick={() => navigate(createLink("/"))}
              className="cursor-pointer !w-32"
              src={reckittIcon}
              alt="Reckitt"
            />
          </span>
        </div>
        <button
          type="button"
          className="sidebar__icon-wrap__hide-sidebar"
          onClick={() => setIsCollapsed((prev) => !prev)}
        >
          {isCollapsed ? <IconMenuOpen /> : <IconHideSidebar />}
        </button>
      </div>

      <nav className="sidebar__nav">
        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          {filteredNavigation.map((item, index) => (
            <div key={item.segment}>
              <NavItem
                item={item}
                depth={0}
                openParent={openParent}
                setOpenParent={setOpenParent}
              />
              {index !== filteredNavigation.length - 1 && (
                <Divider
                  sx={{
                    backgroundColor: "#E1E1E1",
                    height: "1px",
                    my: "8px",
                  }}
                />
              )}
            </div>
          ))}
        </List>
      </nav>

      <div className="mt-auto mb-4 logout-item">
        <NavItem
          item={LOGOUT_ITEM}
          depth={0}
          openParent={openParent}
          setOpenParent={setOpenParent}
        />
      </div>
    </div>
  );
}

// --- Sub-component NavItem ---

/**
 * NavItem renders a single navigation node and handles:
 * - active state detection (including nested children)
 * - expand/collapse for items with children
 * - logout flow handling for the special "logout" item
 * - icon/color transitions for hover and active states
 */
function NavItem({
  item,
  depth,
  openParent,
  setOpenParent,
  centerText,
}: NavItemProps) {
  const [hover, setHover] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { createLink } = useSimulationParams();

  const hasChildren = !!(item.children && item.children.length > 0);
  const isLogout = item.segment === "logout";

  // Helper to check if path is active
  const isPathActive = (node: NavItemType): boolean => {
    const currentPathSegment = location.pathname.split("/")[1];
    if (currentPathSegment === node.segment) return true;
    return node.children?.some((child) => isPathActive(child)) ?? false;
  };

  const isActive = isPathActive(item);
  const isOpen = openParent === item.segment;
  const textColor = isActive ? "#E70070" : "#000000";

  /**
   * Clears redirect state, attempts backend logout to clear cookies,
   * resets React Query cache, and navigates to login with a logout flag.
   */
  const handleLogout = async () => {
    sessionStorage.removeItem("redirectUrl");
    try {
      await fetch(`${apiURL}/auth/logout`, {
        method: "GET",
        redirect: "manual",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout failed", error);
    }
    queryClient.clear();
    navigate("/login?logout=success");
  };

  /**
   * Handles click actions:
   * - prevents navigation and triggers logout for the logout item
   * - toggles expansion for items with children
   */
  const handleClick = (e: React.MouseEvent) => {
    if (isLogout) {
      e.preventDefault();
      handleLogout();
    } else if (hasChildren) {
      setOpenParent(isOpen ? null : item.segment);
    }
  };

  /**
   * Renders the list button with routing when applicable.
   * For non-link actions (children or logout), renders a button element.
   */
  const renderButton = () => {
    const IconComponent = ( isActive) && item.hover ? item.hover : item.normal;

    return (
      <ListItemButton
        className={`sidebar__nav-item ${hasChildren ? "sidebar__nav-item--open" : ""}`}
        component={hasChildren || isLogout ? "button" : NavLink}
        // Pass the 'to' prop only when rendering as NavLink
        //to={hasChildren ? undefined : createLink(`/${item.segment}`)}
        {...(!(hasChildren || isLogout) && { to: createLink(`/${item.segment}`) })}
        onClick={handleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{
          pl: 2 + depth * 2 + (hasChildren ? 2 : 0),
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: centerText ? "center" : "flex-start",
          textAlign: centerText ? "center" : "left",

          "& .MuiListItemIcon-root": {
            color: `${textColor}`,
          },
          "& .MuiTypography-root": {
            color: `${textColor}`,
          },
        }}
      >
        {hasChildren && (
          <img
            src={ExpandIcon}
            alt="Expand"
            style={{
              transform: isOpen || isActive ? "rotate(90deg)" : "rotate(0deg)",
              transformOrigin: "center",
              transition: "transform 0.3s ease, filter 0.2s",
              filter:
                hover || isActive
                  ? "brightness(0) saturate(100%) invert(27%) sepia(84%) saturate(4770%) hue-rotate(325deg) brightness(97%) contrast(100%)"
                  : "none",
            }}
          />
        )}

        {IconComponent && (
          <ListItemIcon sx={{ minWidth: 24 }}>
            <IconComponent
              style={{
                width: 24,
                height: 24,
                color: textColor,
                transition: "color 0.2s",
              }}
            />
          </ListItemIcon>
        )}

        <ListItemText
          primary={item.title}
          slotProps={{
            primary: {
              sx: {
                fontWeight: isLogout ? "regular" : "Bold",
                paddingLeft: 1,
                color: textColor,
              },
            },
          }}
        />
      </ListItemButton>
    );
  };

  return (
    <>
      {renderButton()}
      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children?.map((child) => (
              <NavItem
                key={child.segment}
                item={child}
                depth={depth + 1}
                openParent={openParent}
                setOpenParent={setOpenParent}
                centerText
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}
